import {
  Injectable,
  signal,
  PLATFORM_ID,
  inject,
  OnDestroy
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Breakpoint } from './skeleton-grid.types';

export const GRID_BREAKPOINTS = {
  sm: '(min-width: 640px)',
  md: '(min-width: 768px)',
  lg: '(min-width: 1024px)',
  xl: '(min-width: 1280px)'
};

/**
 * Service that tracks viewport changes and broadcasts the active responsive breakpoint
 * strictly using Angular Signals without emitting Observables or triggering unnecessary CD.
 */
@Injectable({ providedIn: 'root' })
export class SkeletonBreakpointService implements OnDestroy {
  /** Signal that contains the currently active responsive breakpoint. */
  activeBreakpoint = signal<Breakpoint>('xs');

  private platformId = inject(PLATFORM_ID);
  private listeners: { mql: MediaQueryList; listener: () => void }[] = [];

  constructor() {
    if (isPlatformBrowser(this.platformId)) {
      this.initListeners();
    }
  }

  private initListeners() {
    const updateBreakpoint = () => {
      // Evaluate matching queries starting from largest to smallest
      if (window.matchMedia(GRID_BREAKPOINTS.xl).matches)
        this.activeBreakpoint.set('xl');
      else if (window.matchMedia(GRID_BREAKPOINTS.lg).matches)
        this.activeBreakpoint.set('lg');
      else if (window.matchMedia(GRID_BREAKPOINTS.md).matches)
        this.activeBreakpoint.set('md');
      else if (window.matchMedia(GRID_BREAKPOINTS.sm).matches)
        this.activeBreakpoint.set('sm');
      else this.activeBreakpoint.set('xs');
    };

    updateBreakpoint();

    // Attach listeners dynamically allowing CSS-less breakpoint awareness
    Object.values(GRID_BREAKPOINTS).forEach((query) => {
      const mql = window.matchMedia(query);
      const listener = () => updateBreakpoint();
      mql.addEventListener('change', listener);
      this.listeners.push({ mql, listener });
    });
  }

  stopListeners(): void {
    this.listeners.forEach(({ mql, listener }) => {
      mql.removeEventListener('change', listener);
    });
  }

  ngOnDestroy() {
    this.stopListeners();
  }
}
